import { Injectable } from '@angular/core';
 import { Observable, of, Subject } from 'rxjs';
 import { catchError, map, tap } from 'rxjs/operators';
 import {
   PlayAnalyticsCreateRequest,
   PlayAnalyticsUpdateRequest,
   PlayAnalyticsResponse,
   PlaybackSessionState
 } from 'src/app/models/play-analytics.model';
 import { BeatsService } from '../beats-service/beats.service';
 import { MobileVerificationFlowService } from '../Auth/mobile-verification-flow.service';
 @Injectable({ providedIn: 'root' })
 export class PlayAnalyticsService {
   private currentSession: PlaybackSessionState | null = null;
   private mobileVerificationBlockedSubject = new Subject<void>();
   mobileVerificationBlocked$ = this.mobileVerificationBlockedSubject.asObservable();
   constructor(private beatsService: BeatsService, private mobileVerificationFlow: MobileVerificationFlowService) {}
   startPlaybackSession(beatId: string, beatDuration: number): Observable<string | null> {
     const now = new Date();

     this.currentSession = {
       beat_id: beatId,
       session_id: null,
       total_beat_duration: 0,
       play_duration: 0,
       seek_duration: 0,
       replay_count: 0,
       seek_count: 0,
       pause_count: 0,
       is_complete: false,
       last_known_position: 0,
       started_at: now,
       last_updated_at: now
     };
 
     const payload: PlayAnalyticsCreateRequest = {
       beat_id: beatId,
       played_at: now.toISOString()
     };
 
     return this.beatsService.createPlayAnalyticsSession(payload).pipe(
       map((res: PlayAnalyticsResponse) => {
         const sessionId = res?.data?.session_id ?? null;
         if (this.currentSession) this.currentSession.session_id = sessionId;
         return sessionId;
       }),
       catchError((error) => {
         if (this.mobileVerificationFlow.triggerFromError(error, 'play', false)) {
           this.mobileVerificationBlockedSubject.next();
         }
         return of(null);
       })
     );
   }

   setTotalBeatDuration(duration: number): void {
     if (this.currentSession) {
       this.currentSession.total_beat_duration = duration || 0;
     }
   }
 
   updatePlaybackTime(currentTime: number): void {
     if (!this.currentSession) return;
 
     const delta = currentTime - this.currentSession.last_known_position;
     if (delta > 0 && delta < 2) {
       this.currentSession.play_duration += delta;
     }
 
     this.currentSession.last_known_position = currentTime;
     this.currentSession.last_updated_at = new Date();
   }
 
   registerSeek(currentTime: number): void {
     if (!this.currentSession) return;
 
     const diff = Math.abs(currentTime - this.currentSession.last_known_position);
     if (diff > 1) {
       this.currentSession.seek_count += 1;
       this.currentSession.seek_duration += diff;
     }
 
     this.currentSession.last_known_position = currentTime;
     this.currentSession.last_updated_at = new Date();
   }
 
   incrementPauseCount(): void {
     if (!this.currentSession) return;
     this.currentSession.pause_count += 1;
     this.currentSession.last_updated_at = new Date();
   }
 
   incrementReplayCount(): void {
     if (!this.currentSession) return;
     this.currentSession.replay_count += 1;
     this.currentSession.last_updated_at = new Date();
   }
 
   sendAnalyticsUpdate(isComplete?: boolean): Observable<PlayAnalyticsResponse | null> {
     if (!this.currentSession?.session_id) return of(null);
 
     if (typeof isComplete === 'boolean') {
       this.currentSession.is_complete = isComplete;
     }
 
     const payload = this.buildUpdatePayload();
     return this.beatsService.updatePlayAnalyticsSession(this.currentSession.session_id, payload)
       .pipe(
         catchError((error) => {
           if (this.mobileVerificationFlow.triggerFromError(error, 'play', false)) {
             this.mobileVerificationBlockedSubject.next();
           }
           return of(null);
         })
       );
  }
   endPlaybackSession(isComplete: boolean): Observable<PlayAnalyticsResponse | null> {
     return this.sendAnalyticsUpdate(isComplete).pipe(
       tap(() => {
         this.currentSession = null;
       })
     );
   }
 
   getCurrentSessionId(): string | null {
     return this.currentSession?.session_id ?? null;
   }
 
   getCurrentSessionUpdatePayload(isComplete = false): PlayAnalyticsUpdateRequest | null {
     if (!this.currentSession?.session_id) return null;
     this.currentSession.is_complete = isComplete;
     return this.buildUpdatePayload();
   }
 
   clearSession(): void {
     this.currentSession = null;
   }
 
   private buildUpdatePayload(): PlayAnalyticsUpdateRequest {
     if (!this.currentSession) return {};
 
     const playPercentage =
       this.currentSession.total_beat_duration > 0
         ? Math.min((this.currentSession.play_duration / this.currentSession.total_beat_duration) * 100, 100)
         : 0;
 
     return {
       replay_count: this.currentSession.replay_count,
       seek_count: this.currentSession.seek_count,
       pause_count: this.currentSession.pause_count,
       play_duration: Number(this.currentSession.play_duration.toFixed(2)),
       seek_duration: Number(this.currentSession.seek_duration.toFixed(2)),
       total_beat_duration: Number(this.currentSession.total_beat_duration.toFixed(2)),
       play_percentage: Number(playPercentage.toFixed(2)),
       is_complete: this.currentSession.is_complete,
       played_at: new Date().toISOString()
     };
   }
 }